import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { Spinner } from '@fluentui/react';
import DashboardFolderWidgetHeader from './DashboardFolderWidgetHeader';
import { PrimaryButton, SecondaryButton } from '../../../../components/Button';
import { getScrollBarStyles } from '../../../../config/styles';
import {
  IDashboardFolderFieldsFragment,
  IDashboardFolderQuery,
  IDashboardFolderQueryVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { dashboardFolderQuery } from '../../../../utils/apollo/dashboards';
import { SUB_FOLDERS_MAX_DEPTH } from '../../../../config/constants';
import DashboardFolderWidgetNewFolder from './DashboardFolderWidgetNewFolder';
import DashboardFolderWidgetFolder from './DashboardFolderWidgetFolder';
import toastMessage from '../../../../utils/toastMessage';
import DashboardFolderWidgetCreateNewAction from './DashboardFolderWidgetCreateNewAction';
import { theme } from '../../../../styles/theme';
import { IButtonAnalyticsProps } from '../../../../components/Button/types';

/**
 * Single component for two features:
 * 1. Publishing dashboard;
 * 2. Moving dashboard.
 */

interface IProps extends IButtonAnalyticsProps {
  onSubmit: (folderId: string | null) => void;
  onCancel: () => void;
  submitLabel: string;
  dashboardName: string;
  defaultFolderId?: string | null;
}

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100%;
`;
const ContentWrapper = styled.div`
  position: relative;
  height: 200px;
  border-top: 1px solid ${theme.colors.border};
  border-bottom: 1px solid ${theme.colors.border};
  overflow-y: auto;
  ${getScrollBarStyles(4)};
`;
const Footer = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 12px 12px 8px;
`;
const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 16px;
  margin-left: auto;
`;
const StyledTemplate = styled.div`
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  color: ${theme.colors.secondary400};
`;

const DashboardFolderWidget = ({
  onSubmit,
  onCancel,
  submitLabel,
  dashboardName,
  defaultFolderId,
  trackAction,
  trackContext,
}: IProps) => {
  const [folderId, setFolderId] = useState<string | null>(
    defaultFolderId || null,
  );
  const [createMode, setCreateMode] = useState(false);

  const { data, loading } = useQuery<
    IDashboardFolderQuery,
    IDashboardFolderQueryVariables
  >(dashboardFolderQuery, {
    variables: {
      id: folderId,
    },
  });
  const path = data?.dashboardFolder.path;
  const subFolders = data?.dashboardFolder.subFolders;
  const templates = data?.dashboardFolder.templates;

  const pathNotEmpty = path && path?.length > 0;
  const parentFolderId = pathNotEmpty ? path[path.length - 1].id : null;

  const handleSubmit = () => {
    if (templates?.some((template) => template.name === dashboardName)) {
      toastMessage({
        message: 'Cannot publish a dashboard with the same name in this folder',
        type: 'error',
      });
      return;
    }

    onSubmit(folderId);
  };

  const handleGoToPreviousFolder = () => {
    setFolderId(pathNotEmpty ? path[path.length - 1].parentId : null);
  };

  const handleCreateFolderClick = () => {
    setCreateMode(true);
  };
  const handleCreateNewFolder = (newFolder: IDashboardFolderFieldsFragment) => {
    setCreateMode(false);
    setFolderId(newFolder.id);
  };
  const handleCancelNewFolder = () => {
    setCreateMode(false);
  };

  const isSubmitDisabled =
    loading ||
    createMode ||
    (defaultFolderId !== undefined && folderId === defaultFolderId);

  return (
    <>
      <DashboardFolderWidgetHeader
        pathName={path?.[path.length - 1]?.name ?? null}
        onGoBack={pathNotEmpty ? handleGoToPreviousFolder : undefined}
      />
      <ContentWrapper>
        {loading ? (
          <SpinnerContainer>
            <Spinner />
          </SpinnerContainer>
        ) : (
          <>
            {createMode && (
              <DashboardFolderWidgetNewFolder
                onSubmit={handleCreateNewFolder}
                onCancel={handleCancelNewFolder}
                parentFolderId={parentFolderId}
              />
            )}
            {subFolders && subFolders?.length > 0 && (
              <>
                {subFolders.map((subFolder) => (
                  <DashboardFolderWidgetFolder
                    key={subFolder.id}
                    name={subFolder.name}
                    onClick={() => setFolderId(subFolder.id)}
                  />
                ))}
              </>
            )}
            {templates && templates?.length > 0 && (
              <>
                {templates.map((template) => (
                  <StyledTemplate key={template.baseId}>
                    {template.name}
                  </StyledTemplate>
                ))}
              </>
            )}
          </>
        )}
      </ContentWrapper>
      <Footer>
        {(!pathNotEmpty || path.length < SUB_FOLDERS_MAX_DEPTH) && (
          <DashboardFolderWidgetCreateNewAction
            onClick={handleCreateFolderClick}
            disabled={createMode}
          />
        )}
        <ActionsContainer>
          <SecondaryButton
            type="button"
            onClick={onCancel}
            disabled={loading || createMode}
            trackContext={trackContext}
            trackAction="cancel"
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            trackContext={trackContext}
            trackAction={trackAction}
          >
            {submitLabel}
          </PrimaryButton>
        </ActionsContainer>
      </Footer>
    </>
  );
};

export default DashboardFolderWidget;
